import { get, set, createStore } from 'idb-keyval'
import { alleStaendeNeu } from '../scheduler/scheduler'
import { BALLONSET_STANDARD } from '../config'
import type { IsoDatum, Kartenstand } from '../scheduler/typen'
import type { GespeicherteAntwort, Profil, Speicher, Tagesrunde } from './typen'

const SCHLUESSEL = {
  profil: 'profil',
  staende: 'staende',
  antworten: 'antworten',
  runden: 'runden',
} as const

/**
 * IndexedDB mit Rueckfall auf localStorage.
 *
 * Der Rueckfall ist kein Luxus: In privaten Fenstern und auf manchen aelteren
 * Tablet-Browsern ist IndexedDB gesperrt oder wirft beim ersten Zugriff. Ohne
 * Rueckfall stuende Lina dann vor einer App, die gar nicht erst startet.
 */
let store: ReturnType<typeof createStore> | null = null
try {
  store = createStore('zahlenflug', 'daten')
} catch {
  store = null
}

async function lesen<T>(schluessel: string): Promise<T | undefined> {
  if (store) {
    try {
      return await get<T>(schluessel, store)
    } catch {
      // Weiter zum Rueckfall.
    }
  }
  try {
    const roh = globalThis.localStorage?.getItem(`zahlenflug:${schluessel}`)
    return roh ? (JSON.parse(roh) as T) : undefined
  } catch {
    return undefined
  }
}

async function schreiben<T>(schluessel: string, wert: T): Promise<void> {
  if (store) {
    try {
      await set(schluessel, wert, store)
      return
    } catch {
      // Weiter zum Rueckfall.
    }
  }
  try {
    globalThis.localStorage?.setItem(`zahlenflug:${schluessel}`, JSON.stringify(wert))
  } catch {
    // Wenn auch das scheitert, laeuft die Runde trotzdem zu Ende. Der
    // Lernstand geht verloren, aber das Kind merkt davon nichts, und eine
    // Fehlermeldung mitten im Spiel waere schlimmer als der Verlust.
  }
}

/** Kennung erzeugen. crypto.randomUUID fehlt auf aelteren TV-Browsern. */
export function neueId(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export class LokalerSpeicher implements Speicher {
  async profilLaden(): Promise<Profil> {
    const vorhanden = await lesen<Profil>(SCHLUESSEL.profil)
    // Felder nachtragen, die es beim Anlegen des Profils noch nicht gab.
    // Wichtig: einzeln mit ?? und nicht als Vorgabe vor dem Spread. Ein
    // Spread setzt auch ein vorhandenes undefined und wuerde den Vorgabewert
    // damit wieder ueberschreiben.
    if (vorhanden) {
      return {
        ...vorhanden,
        tonAn: vorhanden.tonAn ?? true,
        ballonset: vorhanden.ballonset ?? BALLONSET_STANDARD,
        fohlenName: vorhanden.fohlenName ?? '',
        herde: vorhanden.herde ?? {},
      }
    }

    const neu: Profil = {
      id: neueId(),
      spitzname: '',
      raumId: null,
      erstelltAm: new Date().toISOString(),
      tonAn: true,
      ballonset: BALLONSET_STANDARD,
      fohlenName: '',
      herde: {},
    }
    await schreiben(SCHLUESSEL.profil, neu)
    return neu
  }

  async profilSpeichern(profil: Profil): Promise<void> {
    await schreiben(SCHLUESSEL.profil, profil)
  }

  async staendeLaden(heute: IsoDatum): Promise<Kartenstand[]> {
    const vorhanden = await lesen<Kartenstand[]>(SCHLUESSEL.staende)
    if (vorhanden && vorhanden.length > 0) return ergaenzeFehlende(vorhanden, heute)

    const neu = alleStaendeNeu(heute)
    await schreiben(SCHLUESSEL.staende, neu)
    return neu
  }

  async staendeSpeichern(staende: Kartenstand[]): Promise<void> {
    const alle = await lesen<Kartenstand[]>(SCHLUESSEL.staende)
    if (!alle) {
      await schreiben(SCHLUESSEL.staende, staende)
      return
    }
    // Nur die beruehrten Karten ersetzen, der Rest bleibt unangetastet.
    const nachId = new Map(alle.map((s) => [s.karte, s]))
    for (const s of staende) nachId.set(s.karte, s)
    await schreiben(SCHLUESSEL.staende, [...nachId.values()])
  }

  async antwortenAnhaengen(antworten: GespeicherteAntwort[]): Promise<void> {
    // Vollstaendige Historie, nichts wird ueberschrieben oder geloescht. Bei
    // 20 Aufgaben taeglich sind das rund 1 MB pro Jahr, das traegt IndexedDB
    // muehelos. Sobald in Phase 2 ein Server dazukommt, wandern hochgeladene
    // Antworten in ein Archiv.
    const alle = (await lesen<GespeicherteAntwort[]>(SCHLUESSEL.antworten)) ?? []
    await schreiben(SCHLUESSEL.antworten, [...alle, ...antworten])
  }

  async antwortenLaden(): Promise<GespeicherteAntwort[]> {
    return (await lesen<GespeicherteAntwort[]>(SCHLUESSEL.antworten)) ?? []
  }

  async rundenLaden(): Promise<Tagesrunde[]> {
    return (await lesen<Tagesrunde[]>(SCHLUESSEL.runden)) ?? []
  }

  async rundeSpeichern(runde: Tagesrunde): Promise<void> {
    const alle = await this.rundenLaden()
    // Pro Tag genau ein Eintrag. Uebt sie zweimal, zaehlen die Aufgaben zusammen.
    const index = alle.findIndex((r) => r.datum === runde.datum)
    if (index >= 0) {
      const alt = alle[index]!
      alle[index] = {
        ...runde,
        anzahlAufgaben: alt.anzahlAufgaben + runde.anzahlAufgaben,
        geschafft: alt.geschafft || runde.geschafft,
        schnellsteMs: kleinere(alt.schnellsteMs, runde.schnellsteMs),
        laengsteKette: Math.max(alt.laengsteKette, runde.laengsteKette),
      }
    } else {
      alle.push(runde)
    }
    await schreiben(SCHLUESSEL.runden, alle)
  }
}

function kleinere(a: number | null, b: number | null): number | null {
  if (a === null) return b
  if (b === null) return a
  return Math.min(a, b)
}

/**
 * Karten ergaenzen, die im gespeicherten Stand fehlen.
 *
 * Greift, wenn der Kartenbestand spaeter erweitert wird, etwa um das grosse
 * Einmaleins. Ohne das wuerden neue Karten nie gestellt, weil der Scheduler
 * nur kennt, was gespeichert ist.
 */
function ergaenzeFehlende(vorhanden: Kartenstand[], heute: IsoDatum): Kartenstand[] {
  const bekannt = new Set(vorhanden.map((s) => s.karte))
  const fehlend = alleStaendeNeu(heute).filter((s) => !bekannt.has(s.karte))
  return fehlend.length ? [...vorhanden, ...fehlend] : vorhanden
}
